import { contractTypeLabel } from "@etudescarre/constants";
import { Button, Modal, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { personName } from "./contracts-panel";

type ReferentRole = {
  yardId: number;
  yardName: string;
  contractId: number;
  contractType: string;
  contractShortName: string | null;
  performerName: string;
  teamId: number | null;
  teamName: string | null;
};

type Candidate = {
  id: string;
  firstName: string | null;
  lastName: string | null;
};

const roleKey = (role: Pick<ReferentRole, "contractId" | "teamId">) =>
  `${role.contractId}:${role.teamId ?? "contrat"}`;

const roleLabel = (role: ReferentRole) =>
  isNil(role.teamName)
    ? "Référent du contrat"
    : `Référent de l'équipe ${role.teamName}`;

const contractLabel = (role: ReferentRole) => {
  const type = contractTypeLabel(role.contractType);
  return isNil(role.contractShortName)
    ? `${role.performerName} (${type})`
    : `${role.performerName} (${type}, ${role.contractShortName})`;
};

export type DeactivateCollaboratorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: { id: string; name: string };
  /** Deactivation on this contract only (909/910); omitted: in the company (annuaire). */
  contractId?: number;
  /** Annuaire: already deactivated, only their referent roles are handed over. */
  alreadyInactive?: boolean;
  onSuccess: () => void;
};

/**
 * Deactivates a collaborator on a contract or in their company. Each
 * referent role they hold there needs its successor before validating
 * (PO, N28/N74, 30/10): the roles are listed site by site, team by team.
 */
export function DeactivateCollaboratorModal({
  open,
  onOpenChange,
  employee,
  contractId,
  alreadyInactive = false,
  onSuccess,
}: DeactivateCollaboratorModalProps) {
  const [roles, setRoles] = useState<ReferentRole[] | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [successors, setSuccessors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRoles(null);
    setSuccessors({});
    apiClient
      .GET("/api/admin/employees/{id}/referent-roles", {
        params: { path: { id: employee.id }, query: { contractId } },
      })
      .then(({ data, error }) => {
        if (isNotNil(error) || isNil(data)) {
          toaster.error({
            title: "Erreur",
            description: "Impossible de charger les rôles de référent",
          });
          return;
        }
        setRoles(
          [...data.data.roles].sort(
            (a, b) =>
              a.yardName.localeCompare(b.yardName) ||
              a.contractId - b.contractId ||
              (a.teamId ?? 0) - (b.teamId ?? 0),
          ),
        );
        setCandidates(data.data.candidates);
      });
  }, [open, employee.id, contractId]);

  const candidateOptions = candidates.map((c) => ({
    value: c.id,
    label: personName(c.id, c),
  }));
  const missing = (roles ?? []).filter((role) => !successors[roleKey(role)]);
  const blockedByNoCandidate =
    isNotNil(roles) && roles.length > 0 && candidates.length === 0;

  const submit = async () => {
    if (isNil(roles) || missing.length > 0) return;
    setIsSubmitting(true);
    try {
      const { error } = await apiClient.POST(
        "/api/admin/employees/{id}/deactivate",
        {
          params: { path: { id: employee.id } },
          body: {
            contractId,
            replacements: roles.map((role) => ({
              contractId: role.contractId,
              teamId: role.teamId,
              employeeId: successors[roleKey(role)],
            })),
          },
        },
      );
      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error || "Échec de la désactivation",
        });
        return;
      }
      toaster.success({
        title: "Succès",
        description: alreadyInactive
          ? "Rôles de référent réaffectés"
          : isNotNil(contractId)
            ? "Collaborateur désactivé sur ce contrat"
            : "Collaborateur désactivé",
      });
      onOpenChange(false);
      onSuccess();
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = alreadyInactive
    ? `Remplacer ${employee.name}`
    : isNotNil(contractId)
      ? `Désactiver ${employee.name} sur ce contrat`
      : `Désactiver ${employee.name}`;
  const description = alreadyInactive
    ? `${employee.name} est désactivé dans sa société. Désignez qui reprend chacun de ses rôles de référent.`
    : isNotNil(contractId)
      ? `${employee.name} sera désactivé sur ce contrat et sortira de toutes ses équipes.`
      : `${employee.name} sera désactivé dans sa société, donc sur tous ses contrats et dans toutes ses équipes.`;

  // Roles grouped by yard.
  const yards = [...new Map((roles ?? []).map((r) => [r.yardId, r])).values()];

  return (
    <Modal.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Modal.Portal>
        <Modal.Backdrop />
        <Modal.Positioner>
          <Modal.Content size="md">
            <Modal.Header>
              <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="space-y-3 text-sm text-zinc-600 dark:text-zinc-400">
                <p>{description}</p>
                {isNil(roles) ? (
                  <p className="text-zinc-400">Chargement...</p>
                ) : roles.length === 0 ? (
                  !alreadyInactive && (
                    <p>
                      {isNotNil(contractId)
                        ? "Il n'est référent ni du contrat ni de ses équipes."
                        : "Il n'a aucun rôle de référent à reprendre sur un chantier non clôturé."}
                    </p>
                  )
                ) : (
                  <>
                    {!alreadyInactive && (
                      <p>
                        Il est référent : choisissez son remplaçant pour chaque
                        rôle.
                      </p>
                    )}
                    {blockedByNoCandidate && (
                      <p className="font-medium text-red-600 dark:text-red-400">
                        Aucun autre collaborateur actif dans sa société :
                        ajoutez d'abord son remplaçant dans l'annuaire.
                      </p>
                    )}
                    <div className="max-h-96 space-y-3 overflow-y-auto">
                      {yards.map((yard) => (
                        <div key={yard.yardId} className="space-y-2">
                          {isNil(contractId) && (
                            <h4 className="font-medium text-zinc-900 dark:text-zinc-100">
                              {yard.yardName}
                            </h4>
                          )}
                          {roles
                            .filter((role) => role.yardId === yard.yardId)
                            .map((role) => (
                              <div
                                key={roleKey(role)}
                                className="grid grid-cols-2 items-center gap-2"
                              >
                                <span className="min-w-0">
                                  {isNil(contractId) && (
                                    <span
                                      className="block truncate text-xs text-zinc-500"
                                      title={contractLabel(role)}
                                    >
                                      {contractLabel(role)}
                                    </span>
                                  )}
                                  <span className="block text-zinc-900 dark:text-zinc-100">
                                    {roleLabel(role)}
                                  </span>
                                </span>
                                <Select
                                  options={candidateOptions}
                                  value={
                                    successors[roleKey(role)]
                                      ? [successors[roleKey(role)]]
                                      : []
                                  }
                                  onValueChange={(details) =>
                                    setSuccessors((prev) => ({
                                      ...prev,
                                      [roleKey(role)]: details.value[0] ?? "",
                                    }))
                                  }
                                  placeholder="Remplaçant"
                                  disabled={candidates.length === 0}
                                />
                              </div>
                            ))}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              {isNotNil(roles) &&
                missing.length > 0 &&
                !blockedByNoCandidate && (
                  <span className="mr-auto text-xs text-zinc-500">
                    Choisissez un remplaçant pour chaque rôle.
                  </span>
                )}
              <Button
                outline
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Annuler
              </Button>
              <Button
                onClick={submit}
                disabled={isSubmitting || isNil(roles) || missing.length > 0}
              >
                {isSubmitting
                  ? "En cours..."
                  : alreadyInactive
                    ? "Réaffecter"
                    : "Désactiver"}
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Portal>
    </Modal.Root>
  );
}
